import React, { useState, useMemo } from 'react';
import { OKRState, Task } from '../types';
import Card from 'components/card';
import { AlertTriangle, Settings } from 'lucide-react';

// ── Week helpers ──────────────────────────────────────────────────────────────

function getWeekStart(offsetWeeks = 0): Date {
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7) + offsetWeeks * 7);
    monday.setHours(0, 0, 0, 0);
    return monday;
}

function weekLabel(start: Date): string {
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return `${start.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} – ${end.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}`;
}

function isInWeekOf(dateStr: string, weekStart: Date): boolean {
    const d = new Date(dateStr);
    const end = new Date(weekStart);
    end.setDate(weekStart.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return d >= weekStart && d <= end;
}

// ── Cell ──────────────────────────────────────────────────────────────────────

const Cell: React.FC<{
    tasks: Task[];
    capacity: number;
    memberName: string;
    weekLbl: string;
}> = ({ tasks, capacity, memberName, weekLbl }) => {
    const [tooltip, setTooltip] = useState(false);
    const count = tasks.length;
    const done = tasks.filter((t) => t.status === 'done').length;
    const pct = capacity > 0 ? count / capacity : 0;

    const bg =
        pct === 0 ? 'bg-gray-50 text-gray-300 border-gray-100' :
        pct <= 0.6 ? 'bg-green-50 text-green-700 border-green-200' :
        pct <= 1   ? 'bg-orange-50 text-orange-600 border-orange-200' :
                     'bg-red-50 text-red-600 border-red-200';

    return (
        <div
            className="relative"
            onMouseEnter={() => setTooltip(true)}
            onMouseLeave={() => setTooltip(false)}
        >
            <div className={`rounded-xl border text-center py-3 cursor-default select-none ${bg}`}>
                <p className="text-lg font-bold leading-none">{count}</p>
                <p className="text-xs mt-0.5 opacity-70">{done} done</p>
                {pct > 1 && <AlertTriangle size={11} className="mx-auto mt-1 text-red-500" />}
            </div>
            {tooltip && count > 0 && (
                <div className="absolute z-50 bottom-full mb-2 left-1/2 -translate-x-1/2 bg-navy-700 text-white text-xs rounded-xl p-3 w-52 shadow-lg">
                    <p className="font-semibold mb-2">{memberName} — {weekLbl}</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                        {tasks.map((t) => (
                            <div key={t.id} className="flex items-center gap-1.5">
                                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${t.status === 'done' ? 'bg-green-400' : t.status === 'blocked' ? 'bg-red-400' : 'bg-gray-400'}`} />
                                <span className="truncate opacity-90">{t.title}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Main component ────────────────────────────────────────────────────────────

const CapacityPlanning: React.FC<{ state: OKRState }> = ({ state }) => {
    const [capacities, setCapacities] = useState<Record<string, number>>(() => {
        try { return JSON.parse(localStorage.getItem('toola_okr_capacity') ?? '{}'); } catch { return {}; }
    });
    const [editingCapacity, setEditingCapacity] = useState<string | null>(null);
    const [tempCap, setTempCap] = useState('5');

    const weeks = useMemo(() => [0, 1, 2, 3, 4].map((i) => ({
        start: getWeekStart(i),
        label: i === 0 ? `Cette semaine (${weekLabel(getWeekStart(0))})` : weekLabel(getWeekStart(i)),
    })), []);

    const getMemberCapacity = (memberId: string) => capacities[memberId] ?? 5;

    const saveCapacity = (memberId: string) => {
        const cap = Math.max(1, parseInt(tempCap) || 5);
        const next = { ...capacities, [memberId]: cap };
        setCapacities(next);
        localStorage.setItem('toola_okr_capacity', JSON.stringify(next));
        setEditingCapacity(null);
    };

    // Pre-compute member × week → tasks lookup in one useMemo pass.
    // Avoids O(members × weeks × tasks) re-filtering on every render.
    const taskGrid = useMemo(() => {
        const grid = new Map<string, Task[][]>();
        for (const member of state.team) {
            grid.set(
                member.id,
                weeks.map((w) =>
                    state.tasks.filter(
                        (t) => t.assigneeId === member.id && isInWeekOf(t.dueDate, w.start),
                    ),
                ),
            );
        }
        return grid;
    }, [state.team, state.tasks, weeks]);

    const tasksForMemberWeek = (memberId: string, weekIndex: number): Task[] =>
        taskGrid.get(memberId)?.[weekIndex] ?? [];

    const overloadedCount = useMemo(() =>
        state.team.reduce(
            (n, m) => n + (tasksForMemberWeek(m.id, 0).length > getMemberCapacity(m.id) ? 1 : 0),
            0,
        ),
        [taskGrid, capacities, state.team],
    );

    if (state.team.length === 0) {
        return (
            <Card extra="p-8 text-center">
                <p className="text-gray-400 font-medium">Aucun membre dans l'équipe</p>
                <p className="text-sm text-gray-300 mt-1">Connectez-vous à l'API pour voir les collaborateurs</p>
            </Card>
        );
    }

    return (
        <div className="space-y-4">
            {overloadedCount > 0 && (
                <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                    <AlertTriangle size={16} className="flex-shrink-0" />
                    <span><strong>{overloadedCount} membre{overloadedCount > 1 ? 's' : ''}</strong> déjà surchargé{overloadedCount > 1 ? 's' : ''} cette semaine — revoyez la répartition avant d'assigner de nouvelles tâches.</span>
                </div>
            )}

            <Card extra="p-5 overflow-x-auto">
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <h3 className="text-base font-bold text-navy-700">Capacity Planning — 5 semaines</h3>
                        <p className="text-sm text-gray-500 mt-0.5">Nombre de tâches planifiées vs capacité configurable par membre</p>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-100 border border-green-200" /> OK</span>
                        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-100 border border-orange-200" /> Chargé</span>
                        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 border border-red-200" /> Surchargé</span>
                    </div>
                </div>

                {/* Grid */}
                <div style={{ minWidth: '700px' }}>
                    {/* Header row */}
                    <div className="grid mb-2" style={{ gridTemplateColumns: '200px repeat(5, 1fr)', gap: '8px' }}>
                        <div /> {/* empty corner */}
                        {weeks.map((w, i) => (
                            <div key={i} className="text-center text-xs font-semibold text-gray-500 truncate px-1">{w.label}</div>
                        ))}
                    </div>

                    {/* Member rows */}
                    <div className="space-y-2">
                        {state.team.map((member) => {
                            const cap = getMemberCapacity(member.id);
                            return (
                                <div key={member.id} className="grid items-center" style={{ gridTemplateColumns: '200px repeat(5, 1fr)', gap: '8px' }}>
                                    {/* Member info */}
                                    <div className="flex items-center gap-2 pr-2">
                                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: member.avatarColor }}>
                                            {member.avatarInitials}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-semibold text-navy-700 truncate">{member.name}</p>
                                            {editingCapacity === member.id ? (
                                                <div className="flex items-center gap-1 mt-0.5">
                                                    <input
                                                        type="number"
                                                        value={tempCap}
                                                        onChange={(e) => setTempCap(e.target.value)}
                                                        onKeyDown={(e) => e.key === 'Enter' && saveCapacity(member.id)}
                                                        className="w-12 text-xs border border-gray-200 rounded px-1 py-0.5"
                                                        autoFocus
                                                    />
                                                    <button onClick={() => saveCapacity(member.id)} className="text-xs text-toola-500 font-medium">OK</button>
                                                    <button onClick={() => setEditingCapacity(null)} className="text-xs text-gray-400">✕</button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => { setEditingCapacity(member.id); setTempCap(String(cap)); }}
                                                    className="flex items-center gap-0.5 text-xs text-gray-400 hover:text-toola-500 transition-colors mt-0.5"
                                                >
                                                    <Settings size={10} /> Cap. : {cap}/sem
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Week cells — uses pre-computed taskGrid (O(1) lookup) */}
                                    {weeks.map((w, i) => (
                                        <Cell
                                            key={i}
                                            tasks={tasksForMemberWeek(member.id, i)}
                                            capacity={cap}
                                            memberName={member.name}
                                            weekLbl={w.label}
                                        />
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </Card>

            {/* Legend */}
            <Card extra="p-4">
                <p className="text-xs text-gray-500">
                    <strong>Comment lire :</strong> Chaque cellule = nombre de tâches dont la date limite tombe dans cette semaine. La capacité par défaut est <strong>5 tâches/semaine</strong> — cliquez sur le ⚙ pour la personnaliser par membre.
                    Les cellules <span className="text-red-500 font-medium">rouges</span> signalent une surcharge à venir et bloquent la création de nouvelles tâches sur ce membre pour cette semaine.
                </p>
            </Card>
        </div>
    );
};

export default CapacityPlanning;
